import React, { useState } from 'react';

import { Select } from '@aline/common';
import { isEmpty } from 'ramda';
import type { UserDefinedRoleIdToDetails } from 'src/components';
import { AlineButton, AlineDialog, AssignRoleList } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { useNotificationDispatch } from 'src/hooks';
import { useGetUnassignedWorkflowPermissions } from 'src/hooks/assignment';
import { NotificationsActionType } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

interface UnassignedRolesDialogProps {
  onClose: () => void;
  open: boolean;
  workflowId: UUID;
}

const useStyles = makeStyles()(theme => ({
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    padding: theme.spacing(2, 0, 0, 0),
  },
  dialogPaper: {
    width: theme.dialogStyles.mediumDialogWidth,
  },
}));

export function UnassignedRolesDialog({ onClose, open, workflowId }: UnassignedRolesDialogProps) {
  const { classes } = useStyles();
  const dispatch = useNotificationDispatch();
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [assignmentMap, setAssignmentMap] = useState<UserDefinedRoleIdToDetails>({});
  const [changeCollaboratorAssignment] = Apollo.useChangeCollaboratorAssignmentMutation();
  const { unassignedWorkflowPermissions } = useGetUnassignedWorkflowPermissions({ workflowId });

  function handleClose(): void {
    setAssignmentMap({});
    onClose();
  }

  return (
    <AlineDialog
      classes={{ paper: classes.dialogPaper }}
      data-cy={Select.document.s4s.bottomBar.sanityCheck.unassignedRoles.container}
      open={open}
      title="Assign placeholders before sending"
      onClose={handleClose}
    >
      <AssignRoleList
        entityId={workflowId}
        entityType="Workflow"
        filterBy="unassigned"
        onListChange={roleAssignmentMap => setAssignmentMap(roleAssignmentMap)}
      />

      <div className={classes.buttonRow}>
        <AlineButton
          color="primary"
          data-cy={Select.document.s4s.bottomBar.sanityCheck.unassignedRoles.cancelButton}
          disabled={isSubmitting}
          variant="outlined"
          onClick={handleClose}
        >
          Cancel
        </AlineButton>
        <AlineButton
          color="primary"
          data-cy={Select.document.s4s.bottomBar.sanityCheck.unassignedRoles.nextButton}
          disabled={isEmpty(assignmentMap)}
          loading={isSubmitting}
          variant="contained"
          onClick={async () => {
            setSubmitting(true);
            await Promise.all(
              Object.entries(assignmentMap).map(async ([assignedRoleId, details]) => {
                const { email, userId: assignedUserId } = details;
                const permissionId = unassignedWorkflowPermissions.find(
                  permission => permission.role?.id === assignedRoleId
                )?.id;
                if (!permissionId) {
                  dispatch({
                    payload: {
                      key: uuid4(),
                      message: 'Unable to locate permission for updating placeholder.',
                      variant: 'error',
                    },
                    type: NotificationsActionType.PUSH_NOTIFICATION,
                  });
                  return;
                }

                await changeCollaboratorAssignment({
                  variables: {
                    input: {
                      assignedRoleId,
                      assignedUserId,
                      email,
                      permissionId,
                    },
                  },
                });
              })
            );
            setSubmitting(false);
            handleClose();
          }}
        >
          Assign
        </AlineButton>
      </div>
    </AlineDialog>
  );
}
